import { useEffect, useId, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Briefcase, CheckCircle2, ArrowRight, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { sendEvent } from "@/lib/firstPartyAnalytics";

const OPPORTUNITY_TYPES = [
  "Social/content partnership",
  "Brand ambassador",
  "Appearance",
  "Speaking",
  "Product partnership",
  "Community activation",
  "Sponsorship",
  "Other",
];

const BUDGETS = [
  "Under $500",
  "$500–$2,500",
  "$2,500–$10,000",
  "$10,000–$25,000",
  "$25,000+",
  "Not sure yet",
];

/** Event the support hub dispatches to open this single, shared inquiry form. */
export const PARTNERSHIP_EXPAND_EVENT = "siis:partnership-expand";

const emailOk = (v: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);

const field =
  "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm min-h-[44px] outline-none focus:border-primary focus-visible:ring-2 focus-visible:ring-primary transition-colors";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-muted-foreground";

type Errors = Partial<Record<"company" | "contact_name" | "email" | "opportunity_type", string>>;

type Props = {
  athleteId: string;
  athleteName: string;
  partnershipInterests?: string | null;
  /** Demo profiles validate and show the real success state without sending anything. */
  demo?: boolean;
};

const WorkWithAthleteSection = ({ athleteId, athleteName, partnershipInterests, demo = false }: Props) => {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [dirty, setDirty] = useState(false);
  /** Invisible honeypot — only automated submitters ever fill this in. */
  const [honeypot, setHoneypot] = useState("");
  const openBtnRef = useRef<HTMLButtonElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  /**
   * One idempotency key per logical submission. It is reused for every retry
   * and for any unknown outcome (network drop, timeout), so a retry can never
   * create a second inquiry. It is rotated only after a confirmed success or
   * when the visitor deliberately starts a new inquiry.
   */
  const submissionKey = useRef<string>(crypto.randomUUID());


  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    opportunity_type: "",
    estimated_budget: "",
    timing: "",
    description: "",
  });

  const firstName = athleteName.trim().split(" ")[0] || athleteName;

  const expand = () => {
    setOpen(true);
    requestAnimationFrame(() => firstFieldRef.current?.focus());
  };

  // The "Work With Her" action in the support hub targets this same module.
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setTimeout(() => firstFieldRef.current?.focus(), 350);
    };
    window.addEventListener(PARTNERSHIP_EXPAND_EVENT, handler);
    return () => window.removeEventListener(PARTNERSHIP_EXPAND_EVENT, handler);
  }, []);

  const close = () => {
    setOpen(false);
    openBtnRef.current?.focus();
  };

  const set =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setDirty(true);
      setForm((f) => ({ ...f, [k]: e.target.value }));
      setErrors((prev) => ({ ...prev, [k]: undefined }));
    };

  const validate = (): Errors => {
    const e: Errors = {};
    if (!form.company.trim()) e.company = "Please enter the company or organization.";
    if (!form.contact_name.trim()) e.contact_name = "Please enter a contact name.";
    if (!emailOk(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.opportunity_type) e.opportunity_type = "Please choose a type of opportunity.";
    return e;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the highlighted fields.");
      document.getElementById(`${uid}-${Object.keys(errs)[0]}`)?.focus();
      return;
    }

    if (demo) {
      setDirty(false);
      setSent(true);
      return;
    }

    setSaving(true);
    // Submissions go through a protected service that validates and rate-limits
    // server-side; the browser cannot write inquiries directly.
    const { data, error } = await supabase.functions.invoke("submit-inquiry", {
      body: {
        submission_key: submissionKey.current,
        athlete_id: athleteId,

        company: form.company,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        opportunity_type: form.opportunity_type,
        estimated_budget: form.estimated_budget,
        timing: form.timing,
        description: form.description,
        website: honeypot,
      },
    });
    setSaving(false);

    if (error) {
      let payload: { error?: string; fieldErrors?: Errors } | null = null;
      try {
        payload = await (error as { context?: Response }).context?.json();
      } catch {
        payload = null;
      }
      if (payload?.fieldErrors) {
        setErrors(payload.fieldErrors);
        document.getElementById(`${uid}-${Object.keys(payload.fieldErrors)[0]}`)?.focus();
      }
      toast.error(payload?.error ?? "Something went wrong. Please try again.");
      // The key is deliberately NOT rotated: the next attempt is the same
      // logical submission and must not be able to create a second inquiry.
      return;
    }

    if (data && typeof data === "object" && "error" in data) {
      toast.error(String((data as { error: string }).error));
      return;
    }

    // Confirmed success: any later inquiry is a genuinely new one.
    submissionKey.current = crypto.randomUUID();
    setDirty(false);
    setSent(true);
    trackEvent("inquiry_submitted", { opportunity_type: form.opportunity_type });
    sendEvent("inquiry_submit", { athleteId, metadata: { opportunity_type: form.opportunity_type } });

  };


  const err = (k: keyof Errors) =>
    errors[k] ? (
      <p id={`${uid}-${k}-error`} role="alert" className="mt-1.5 text-xs font-medium text-destructive">
        {errors[k]}
      </p>
    ) : null;

  const aria = (k: keyof Errors) => ({
    "aria-invalid": errors[k] ? true : undefined,
    "aria-describedby": errors[k] ? `${uid}-${k}-error` : undefined,
  });

  return (
    <section
      id="work-with"
      aria-labelledby={`${uid}-heading`}
      className="bg-card rounded-[2rem] border border-border shadow-card p-6 md:p-8 max-w-3xl"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Partnerships</p>
      <h2 id={`${uid}-heading`} className="font-display text-2xl md:text-3xl uppercase leading-tight mt-2 text-balance break-words">
        Bring Her an Opportunity
      </h2>
      <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
        Invite {firstName} to collaborate, speak, appear, create content or represent your brand.
      </p>

      {partnershipInterests && (
        <p className="mt-4 flex gap-2 text-sm leading-relaxed break-words">
          <Briefcase className="w-4 h-4 text-primary shrink-0 mt-0.5" aria-hidden />
          <span>
            <span className="font-semibold">Open to:</span> {partnershipInterests}
          </span>
        </p>
      )}

      {!open && !sent && (
        <button
          ref={openBtnRef}
          type="button"
          onClick={expand}
          aria-expanded={false}
          aria-controls={`${uid}-form`}
          className="mt-5 inline-flex min-h-[44px] items-center justify-center gap-2 px-6 py-3 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-primary hover:text-primary-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          Start a partnership conversation
          <ArrowRight className="w-4 h-4" aria-hidden />
        </button>
      )}

      {sent && (
        <div className="mt-5 rounded-2xl bg-muted p-5 flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-primary shrink-0" aria-hidden />
          <div role="status">
            <p className="font-semibold">
              {demo
                ? "Demo profile: this is the confirmation a brand sees. Nothing was sent."
                : "Thanks! Your inquiry has been sent to Say It In Sports. We'll be in touch."}
            </p>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setOpen(true);
                setForm({ company: "", contact_name: "", email: "", phone: "", opportunity_type: "", estimated_budget: "", timing: "", description: "" });
                requestAnimationFrame(() => firstFieldRef.current?.focus());
              }}
              className="mt-3 text-sm font-semibold text-primary underline min-h-[44px]"
            >
              Submit another inquiry
            </button>
          </div>
        </div>
      )}

      {open && !sent && (
        <form id={`${uid}-form`} noValidate onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4">
          {/* Honeypot: hidden from sight and from assistive technology. */}
          <div aria-hidden className="hidden">
            <label htmlFor={`${uid}-website`}>Website</label>
            <input
              id={`${uid}-website`}
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2 flex items-center justify-between gap-3">

            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Inquiry for {athleteName}
            </p>
            <button
              type="button"
              onClick={close}
              className="inline-flex min-h-[44px] items-center gap-1 px-3 text-sm font-semibold text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full"
            >
              <X className="w-4 h-4" aria-hidden />
              Close
            </button>
          </div>

          <div className="min-w-0">
            <label className={labelCls} htmlFor={`${uid}-company`}>Company / Organization</label>
            <input ref={firstFieldRef} id={`${uid}-company`} className={`${field} mt-1.5`} value={form.company} onChange={set("company")} maxLength={200} required {...aria("company")} />
            {err("company")}
          </div>
          <div className="min-w-0">
            <label className={labelCls} htmlFor={`${uid}-contact_name`}>Contact Name</label>
            <input id={`${uid}-contact_name`} className={`${field} mt-1.5`} value={form.contact_name} onChange={set("contact_name")} maxLength={200} required {...aria("contact_name")} />
            {err("contact_name")}
          </div>
          <div className="min-w-0">
            <label className={labelCls} htmlFor={`${uid}-email`}>Email</label>
            <input id={`${uid}-email`} type="email" inputMode="email" autoComplete="email" className={`${field} mt-1.5`} value={form.email} onChange={set("email")} maxLength={255} required {...aria("email")} />
            {err("email")}
          </div>
          <div className="min-w-0">
            <label className={labelCls} htmlFor={`${uid}-phone`}>Phone (optional)</label>
            <input id={`${uid}-phone`} type="tel" inputMode="tel" autoComplete="tel" className={`${field} mt-1.5`} value={form.phone} onChange={set("phone")} maxLength={50} />
          </div>
          <div className="min-w-0">
            <label className={labelCls} htmlFor={`${uid}-athlete`}>Athlete</label>
            <input id={`${uid}-athlete`} className={`${field} mt-1.5 bg-muted`} value={athleteName} readOnly />
          </div>
          <div className="min-w-0">
            <label className={labelCls} htmlFor={`${uid}-opportunity_type`}>Type of Opportunity</label>
            <select id={`${uid}-opportunity_type`} className={`${field} mt-1.5`} value={form.opportunity_type} onChange={set("opportunity_type")} required {...aria("opportunity_type")}>
              <option value="">Select…</option>
              {OPPORTUNITY_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {err("opportunity_type")}
          </div>
          <div className="min-w-0">
            <label className={labelCls} htmlFor={`${uid}-budget`}>Estimated Budget</label>
            <select id={`${uid}-budget`} className={`${field} mt-1.5`} value={form.estimated_budget} onChange={set("estimated_budget")}>
              <option value="">Select…</option>
              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="min-w-0">
            <label className={labelCls} htmlFor={`${uid}-timing`}>Campaign / Event Timing</label>
            <input id={`${uid}-timing`} className={`${field} mt-1.5`} value={form.timing} onChange={set("timing")} maxLength={200} placeholder="e.g. Spring 2027" />
          </div>
          <div className="sm:col-span-2 min-w-0">
            <label className={labelCls} htmlFor={`${uid}-desc`}>Tell Us About the Opportunity</label>
            <textarea id={`${uid}-desc`} rows={5} className={`${field} mt-1.5 resize-y`} value={form.description} onChange={set("description")} maxLength={5000} />
          </div>
          <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto min-h-[44px] px-8 py-3 rounded-full bg-secondary text-secondary-foreground font-display text-base uppercase tracking-wide hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {saving ? "Sending…" : "Send Inquiry"}
            </button>
            <button
              type="button"
              onClick={close}
              className="w-full sm:w-auto min-h-[44px] px-6 py-3 rounded-full border border-border text-sm font-semibold hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Cancel
            </button>
          </div>
          {dirty && (
            <p className="sm:col-span-2 text-xs text-muted-foreground">
              Closing keeps what you've typed — you can reopen this form and finish later.
            </p>
          )}
        </form>
      )}
    </section>
  );
};

export default WorkWithAthleteSection;
